import { $getSelection, $isElementNode, $isRangeSelection, $isTextNode, TextNode } from 'lexical';

import { formatFaxNumberInput } from '@/shared/lib/formatters';

type SelectionPoint = {
  key: string;
  offset: number;
};

type SelectionSnapshot = {
  anchor: SelectionPoint | null;
  focus: SelectionPoint | null;
};

function selectionSnapshotForNodes(textNodes: ReadonlyArray<TextNode>): SelectionSnapshot {
  const selection = $getSelection();
  if (!$isRangeSelection(selection)) {
    return { anchor: null, focus: null };
  }

  const nodeOffsets = new Map<string, number>();
  let offset = 0;
  for (const textNode of textNodes) {
    nodeOffsets.set(textNode.getKey(), offset);
    offset += textNode.getTextContentSize();
  }

  const point = (candidate: SelectionPoint): SelectionPoint | null => {
    const nodeOffset = nodeOffsets.get(candidate.key);
    return nodeOffset === undefined
      ? null
      : { key: candidate.key, offset: nodeOffset + candidate.offset };
  };
  return { anchor: point(selection.anchor), focus: point(selection.focus) };
}

function restoreSelection(
  previousSelection: SelectionSnapshot,
  mapPoint: (point: SelectionPoint) => SelectionPoint
): void {
  if (previousSelection.anchor === null && previousSelection.focus === null) {
    return;
  }

  const selection = $getSelection();
  if (!$isRangeSelection(selection)) {
    return;
  }

  if (previousSelection.anchor !== null) {
    const nextAnchor = mapPoint(previousSelection.anchor);
    selection.anchor.set(nextAnchor.key, nextAnchor.offset, 'text');
  }
  if (previousSelection.focus !== null) {
    const nextFocus = mapPoint(previousSelection.focus);
    selection.focus.set(nextFocus.key, nextFocus.offset, 'text');
  }
}

function mapFaxValueOffset(
  currentText: string,
  nextText: string,
  valueStart: number,
  offset: number
): number {
  if (offset >= currentText.length) {
    return nextText.length;
  }
  if (offset <= valueStart) {
    return Math.min(offset, nextText.length);
  }

  const formattedPrefix = formatFaxNumberInput(currentText.slice(valueStart, offset));
  return Math.min(valueStart + formattedPrefix.length, nextText.length);
}

export function formatFaxLineValue(textNode: TextNode): void {
  const parent = textNode.getParent();
  if (parent === null || !$isElementNode(parent)) {
    return;
  }

  const line = parent.getTextContent();
  const match = line.match(/^(\s*Fax:\s*)(.*)$/i);
  if (match === null) {
    return;
  }

  const valueNodes = parent
    .getChildren()
    .filter($isTextNode)
    .filter((child) => !child.getTextContent().match(/^\s*Fax:/i));
  const isLabelAndValueNode = /^\s*Fax:(.*)$/i.test(textNode.getTextContent());
  const firstValueNode = valueNodes[0];
  const isFirstValueNode = firstValueNode?.getKey() === textNode.getKey();

  if (isLabelAndValueNode) {
    const nodeText = textNode.getTextContent();
    const labelMatch = nodeText.match(/^(\s*Fax:)(.*)$/i);
    if (labelMatch === null || (labelMatch[2] ?? '') === '') {
      return;
    }

    const previousKey = textNode.getKey();
    const previousSelection = selectionSnapshotForNodes([textNode]);
    const label = labelMatch[1] ?? '';
    const rawValue = labelMatch[2] ?? '';
    const formattedValue = formatFaxNumberInput(rawValue);
    const nextText = `${label} ${formattedValue}`;
    if (nodeText === nextText) {
      return;
    }

    textNode.setTextContent(nextText);
    restoreSelection(previousSelection, (point) => ({
      key: previousKey,
      offset:
        point.offset <= label.length
          ? point.offset
          : label.length +
            1 +
            mapFaxValueOffset(rawValue, formattedValue, 0, point.offset - label.length),
    }));
    return;
  }

  if (!isFirstValueNode || firstValueNode === undefined) {
    return;
  }

  const currentText = valueNodes.map((node) => node.getTextContent()).join('');
  const currentLeadingWhitespace = currentText.match(/^\s*/)?.[0] ?? '';
  const formattedValue = formatFaxNumberInput(currentText);
  const nextValueText = `${currentLeadingWhitespace}${formattedValue}`;
  const hasInlineFormatting = valueNodes.some((node) => node.getFormat() !== 0);
  if (valueNodes.length === 1 && currentText === nextValueText && !hasInlineFormatting) {
    return;
  }

  const previousSelection = selectionSnapshotForNodes(valueNodes);
  firstValueNode.setTextContent(nextValueText);
  firstValueNode.setFormat(0);
  for (const valueNode of valueNodes.slice(1)) {
    valueNode.remove();
  }
  restoreSelection(previousSelection, (point) => ({
    key: firstValueNode.getKey(),
    offset: mapFaxValueOffset(
      currentText,
      nextValueText,
      currentLeadingWhitespace.length,
      point.offset
    ),
  }));
}
